import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAfiliados } from '../../reducers/afiliado_slice';
import Table from 'react-bootstrap/Table';
import CustomPrintModal from '../UI/CustomPrintModal1';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { fetchSituaciones } from '../../reducers/situacion_slice';
import { apellidosNombreFullPersona, formatearFechaSimple } from '../../util/customFormatter';
import { fechaHoraActualBoliviaSoloVista, getCuotaNombre } from '../../util/valueCalculator';
import { Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { fetchCuotas } from '../../reducers/cuota_slice';

const optionCuotas = [
  { value: "", label: "CAT y CAB" },
  { value: "cat", label: "CAT" },
  { value: "cab", label: "CAB" }
];

const AfiliadoReport = () => {
  const dispatch = useDispatch();
  const afiliados = useSelector(state => state.afiliado.afiliados || []);
  const situaciones = useSelector(state => state.situacion.situaciones || []);
  const cuotas = useSelector(state => state.cuota.cuotas || []);

  const [selectedSituaciones, setSelectedSituaciones] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [], title2: '', title3: '' });
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [includeSignatureLines, setIncludeSignatureLines] = useState(false);
  const [includeSinDeuda, setIncludeSinDeuda] = useState(false); // NUEVO: incluir también sin deuda
  const [customTitle, setCustomTitle] = useState('');
  const [cumpleañeros, setCumpleañeros] = useState(false);

  // ✅ NUEVO: mostrar SOLO los que NO tienen deuda (deudaTotal = 0)
  const [soloSinDeuda, setSoloSinDeuda] = useState(false);

  // Filtros efectivos (los que se usan para el cálculo)
  const [cuotaFinal, setCuotaFinal] = useState("");
  const [typeReport, setTypeReport] = useState('');
  // Filtros en los controles (edición)
  const [cuotaFinalInput, setCuotaFinalInput] = useState("");
  const [typeReportInput, setTypeReportInput] = useState('');
  // Estado de aplicación de filtros
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [isPreparingPrint, setIsPreparingPrint] = useState(false);

  useEffect(() => {
    dispatch(fetchAfiliados());
    dispatch(fetchSituaciones());
  }, [dispatch]);

  useEffect(() => {
    if (situaciones) {
      const situSelectedDefect = [...situaciones]
        .filter((situ) => situ.alias === "activo")
        .map(situ => ({ value: situ.id, label: situ.nombre }));
      setSelectedSituaciones(situSelectedDefect);
    }
  }, [situaciones]);

  // Inicializar cuotaFinal por defecto una sola vez
  useEffect(() => {
    if (!cuotaFinal && afiliados.length > 0) {
      const hoy = new Date();
      let mesEvaluado = hoy.getMonth()+1;
      let anioEvaluado = hoy.getFullYear();

      if (mesEvaluado > 12 || mesEvaluado<1) {
        mesEvaluado = 12; // enero
      }
      if (anioEvaluado <2020) {
        anioEvaluado = 2025;
      }

      const defaultCuota = `${anioEvaluado}-${String(mesEvaluado).padStart(2, '0')}`;
      console.log("mesEvaluado=",mesEvaluado," anioEvaluado=",anioEvaluado," defaultCuota=",defaultCuota)
      setCuotaFinal(defaultCuota);
      setCuotaFinalInput(defaultCuota);
    }
  }, [cuotaFinal, afiliados]);

  const handleFilterSituacionChange = selected => {
    setSelectedSituaciones(selected || []);
  };

  const getCuotasPendientes = (afiliado, fechaEvaluada) => {

    const calculateMonthDifference = (start, end) => {
      if (!start || !end) {
        return 0;
      }

      console.log("Fechas afiliado", apellidosNombreFullPersona(afiliado.persona, "ci"));
      console.log("Fechas comparadas", start, end);
      const [startYear, startMonth] = start.split('-').map(Number);
      const [endYear, endMonth] = end.split('-').map(Number);

      if (isNaN(startYear) || isNaN(startMonth) || isNaN(endYear) || isNaN(endMonth)) {
        return 0;
      }

      return (endYear - startYear) * 12 + (endMonth - startMonth);
    };

    const diffCat = calculateMonthDifference(fechaEvaluada, afiliado.sig_cuota_cat);
    const diffCab = calculateMonthDifference(fechaEvaluada, afiliado.sig_cuota_cab);

    return { diffCat, diffCab };
  };

  const afiliadosConPendientes = useMemo(() => {
    if (Array.isArray(afiliados)) {
      const hoy = new Date();
      let mesEvaluado = hoy.getMonth() + 1;
      let anioEvaluado = hoy.getFullYear();

      if (mesEvaluado > 11) {
        mesEvaluado = 0; // enero
        anioEvaluado += 1;
      }

      const mesString = String(mesEvaluado + 1).padStart(2, '0');
      const fechaEvaluada = `${anioEvaluado}-${mesString}`;

      return [...afiliados].map((afiliado) => {
        const { diffCat, diffCab } = getCuotasPendientes(afiliado, fechaEvaluada);
        return { ...afiliado, diffCat, diffCab };
      });
    } else { return []; }
  }, [afiliados]);

  // Calcular deudas CAT/CAB/Total por afiliado según cuotaFinal + typeReport
  const afiliadosConDeuda = useMemo(() => {
    if (!Array.isArray(afiliadosConPendientes)) return [];

    return afiliadosConPendientes.map(afiliado => {
      let deudaCat = 0;
      let deudaCab = 0;

      if (cuotaFinal && cuotas.length > 0) {
        cuotas
          .filter(c =>
            c.id_afiliado === afiliado.id &&
            (!cuotaFinal || (c.cuota && c.cuota <= cuotaFinal))
          )
          .forEach(c => {
            const saldoCat = Math.max(
              0,
              (Number(c.monto_cat) || 0) - (Number(c.monto_cat_pagada) || 0)
            );
            const saldoCab = Math.max(
              0,
              (Number(c.monto_cab) || 0) - (Number(c.monto_cab_pagada) || 0)
            );
            deudaCat += saldoCat;
            deudaCab += saldoCab;
          });
      }

      // Aplicar tipo de cuota efectivo
      if (typeReport === 'cat') deudaCab = 0;
      if (typeReport === 'cab') deudaCat = 0;

      const deudaTotal = deudaCat + deudaCab;

      return {
        ...afiliado,
        deudaCat,
        deudaCab,
        deudaTotal
      };
    });
  }, [afiliadosConPendientes, cuotas, cuotaFinal, typeReport]);

  const filteredAfiliados = useMemo(() => {
    let result = afiliadosConDeuda;
    if (cumpleañeros) {
  const today = new Date();
  const todayMonth = today.getMonth() + 1;

  setCustomTitle("CUMPLEAÑEROS DEL MES");

  result = result.filter(afiliado => {
    const fechaNacStr = afiliado.persona.fecha_nac;
    if (!fechaNacStr) return false;

    const [year, month, day] = fechaNacStr.split('-');
    if (!year || !month || !day) return false;

    return parseInt(month, 10) === todayMonth;
  });
} else {
      result = result.filter((afiliado) => {
        let matchesCuota = true, matchesSitu = true;

        // SOLO filtramos por deuda cuando hay cuotaFinal y NO se ha activado "incluir sin deuda"
        if (cuotaFinal && !includeSinDeuda) {
          const deudaCat = afiliado.deudaCat || 0;
          const deudaCab = afiliado.deudaCab || 0;
          if (!typeReport) {
            matchesCuota = (deudaCat > 0 || deudaCab > 0);
          } else if (typeReport === "cat") {
            matchesCuota = deudaCat > 0;
          } else { // "cab"
            matchesCuota = deudaCab > 0;
          }
        }

        if (selectedSituaciones.length > 0) {
          matchesSitu = selectedSituaciones.some(situ => situ.value === afiliado.id_situacion);
        }
        return matchesCuota && matchesSitu;
      });
    }

    // ✅ NUEVO: SOLO SIN DEUDA (deudaTotal 0) - se aplica al final sin romper lo existente
    if (soloSinDeuda) {
      result = result.filter(af => Number(af.deudaTotal || 0) === 0);
    }

    return result;
  }, [afiliadosConDeuda, cuotaFinal, typeReport, selectedSituaciones, cumpleañeros, includeSinDeuda, soloSinDeuda]);

  const sortedAfiliados = useMemo(() => {
    const sorted = [...filteredAfiliados];
    sorted.sort((a, b) => {
      let aValue, bValue;
      if (sortConfig.key === 'num_reg') {
        aValue = parseInt(a.num_reg, 10);
        bValue = parseInt(b.num_reg, 10);
      } else {
        aValue = `${apellidosNombreFullPersona(a.persona)}`.toLowerCase();
        bValue = `${apellidosNombreFullPersona(b.persona)}`.toLowerCase();
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredAfiliados, sortConfig]);

  // Totales generales (para tabla, PDF y Excel)
  const { totalDeudaCat, totalDeudaCab, totalDeudaTotal } = useMemo(() => {
    let tCat = 0, tCab = 0, tTot = 0;
    sortedAfiliados.forEach(af => {
      tCat += Number(af.deudaCat || 0);
      tCab += Number(af.deudaCab || 0);
      tTot += Number(af.deudaTotal || 0);
    });
    return { totalDeudaCat: tCat, totalDeudaCab: tCab, totalDeudaTotal: tTot };
  }, [sortedAfiliados]);

  const handleSort = key => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // EXPORTACIÓN A EXCEL - igual a la tabla + fila TOTAL GENERAL
  const handleExport = () => {
    const exportData = sortedAfiliados.map((afiliado, index) => {
      const nombreCompleto = apellidosNombreFullPersona(afiliado.persona);
      const siguienteCat = (!typeReport || typeReport === "cat")
        ? `CAT: ${afiliado.situacion.alias === "activo2" ? "sin pendientes" : getCuotaNombre(afiliado.sig_cuota_cat)}`
        : "";
      const siguienteCab = (!typeReport || typeReport === "cab")
        ? `CAB: ${afiliado.situacion.alias === "activo2" ? "sin pendientes" : getCuotaNombre(afiliado.sig_cuota_cab)}`
        : "";
      const siguienteCuotaTexto = `${siguienteCat}${siguienteCat && siguienteCab ? " | " : ""}${siguienteCab}`;

      const row = {};

      row['#'] = index + 1;

      if (sortConfig.key === 'num_reg') {
        row['Número Registro'] = afiliado.num_reg;
        row['Nombre Completo'] = nombreCompleto;
      } else {
        row['Nombre Completo'] = nombreCompleto;
        row['Número Registro'] = afiliado.num_reg;
      }

      row['C.I.'] = afiliado.persona.ci;
      row['Fecha Registro C.A.B.'] = formatearFechaSimple(afiliado.fecha_reg_cab);

      if (cumpleañeros) {
        row['Fecha Nacimiento'] = formatearFechaSimple(afiliado.persona.fecha_nac);
      }

      row['Residencia'] = afiliado.lugar_residencia?.nombre || '';
      row['Situación'] = afiliado.situacion.nombre || 'N/A';

      if (cuotaFinal && (!typeReport || typeReport === 'cat')) {
        row['Deuda CAT'] = Number(afiliado.deudaCat || 0).toFixed(2);
      }
      if (cuotaFinal && (!typeReport || typeReport === 'cab')) {
        row['Deuda CAB'] = Number(afiliado.deudaCab || 0).toFixed(2);
      }
      if (cuotaFinal) {
        row['Deuda Total'] = Number(afiliado.deudaTotal || 0).toFixed(2);
      }

      row['Siguiente Cuota'] = siguienteCuotaTexto || '';

      return row;
    });

    // Fila TOTAL GENERAL (solo tiene sentido si hay deudas calculadas)
    if (cuotaFinal && sortedAfiliados.length > 0) {
      const totalRow = {};

      totalRow['#'] = '';
      if (sortConfig.key === 'num_reg') {
        totalRow['Número Registro'] = '';
        totalRow['Nombre Completo'] = 'TOTAL GENERAL';
      } else {
        totalRow['Nombre Completo'] = 'TOTAL GENERAL';
        totalRow['Número Registro'] = '';
      }
      totalRow['C.I.'] = '';
      totalRow['Fecha Registro C.A.B.'] = '';
      if (cumpleañeros) {
        totalRow['Fecha Nacimiento'] = '';
      }
      totalRow['Residencia'] = '';
      totalRow['Situación'] = '';

      if (cuotaFinal && (!typeReport || typeReport === 'cat')) {
        totalRow['Deuda CAT'] = totalDeudaCat.toFixed(2);
      }
      if (cuotaFinal && (!typeReport || typeReport === 'cab')) {
        totalRow['Deuda CAB'] = totalDeudaCab.toFixed(2);
      }
      if (cuotaFinal) {
        totalRow['Deuda Total'] = totalDeudaTotal.toFixed(2);
      }

      totalRow['Siguiente Cuota'] = '';

      exportData.push(totalRow);
    }

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Afiliados');
    XLSX.writeFile(workbook, 'afiliados.xlsx');
  };

  // PREVIEW PARA PDF - mismos datos que la tabla + fila TOTAL GENERAL
  const getPreviewContent = () => {
    const headRow = ['#'];

    if (sortConfig.key === 'num_reg') {
      headRow.push('Número Registro', 'Nombre Completo');
    } else {
      headRow.push('Nombre Completo', 'Número Registro');
    }

    headRow.push('C.I.', 'Fecha Registro C.A.B.');

    if (cumpleañeros) {
      headRow.push('Fecha Nacimiento');
    }

    headRow.push('Residencia', 'Situación');

    if (cuotaFinal && (!typeReport || typeReport === 'cat')) {
      headRow.push('Deuda CAT');
    }
    if (cuotaFinal && (!typeReport || typeReport === 'cab')) {
      headRow.push('Deuda CAB');
    }
    if (cuotaFinal) {
      headRow.push('Deuda Total');
    }

    // headRow.push('Siguiente Cuota');

    if (includeSignatureLines) {
      headRow.push('Firma');
    }

    const bodyContent = sortedAfiliados.map((afiliado, index) => {
      const nombreCompleto = apellidosNombreFullPersona(afiliado.persona);

      const row = [];

      row.push(index + 1);

      if (sortConfig.key === 'num_reg') {
        row.push(afiliado.num_reg, nombreCompleto);
      } else {
        row.push(nombreCompleto, afiliado.num_reg);
      }

      row.push(
        afiliado.persona.ci,
        formatearFechaSimple(afiliado.fecha_reg_cab)
      );

      if (cumpleañeros) {
        row.push(formatearFechaSimple(afiliado.persona.fecha_nac));
      }

      row.push(
        afiliado.lugar_residencia?.nombre || '',
        afiliado.situacion.nombre || 'N/A'
      );

      if (cuotaFinal && (!typeReport || typeReport === 'cat')) {
        row.push(Number(afiliado.deudaCat || 0).toFixed(2));
      }
      if (cuotaFinal && (!typeReport || typeReport === 'cab')) {
        row.push(Number(afiliado.deudaCab || 0).toFixed(2));
      }
      if (cuotaFinal) {
        row.push(Number(afiliado.deudaTotal || 0).toFixed(2));
      }

      if (includeSignatureLines) {
        row.push('                       ');
      }

      return row;
    });

    // Fila TOTAL GENERAL en el PDF
    if (cuotaFinal && sortedAfiliados.length > 0) {
      const totalRow = new Array(headRow.length).fill('');
      const nombreIndex = headRow.indexOf('Nombre Completo');
      const deudaCatIndex = headRow.indexOf('Deuda CAT');
      const deudaCabIndex = headRow.indexOf('Deuda CAB');
      const deudaTotalIndex = headRow.indexOf('Deuda Total');

      if (nombreIndex !== -1) {
        totalRow[nombreIndex] = 'TOTAL GENERAL';
      }
      if (deudaCatIndex !== -1) {
        totalRow[deudaCatIndex] = totalDeudaCat.toFixed(2);
      }
      if (deudaCabIndex !== -1) {
        totalRow[deudaCabIndex] = totalDeudaCab.toFixed(2);
      }
      if (deudaTotalIndex !== -1) {
        totalRow[deudaTotalIndex] = totalDeudaTotal.toFixed(2);
      }

      bodyContent.push(totalRow);
    }

    return {
      head: [headRow],
      body: bodyContent,
      title2: customTitle || 'SIN TÍTULO',
      title3: new Date().toLocaleDateString(),
    };
  };

  const handleCuotaChange = (e) => {
    const value = e.target.value;
    setCuotaFinalInput(value);
  };

  const handleApplyFilters = async () => {
    if (cuotaFinalInput) {
      const regex = /^\d{4}-(0[1-9]|1[0-2])$/;
      if (!regex.test(cuotaFinalInput)) {
        Swal.fire(
          "Formato inválido",
          "La fecha debe estar en el formato AAAA-MM (por ejemplo, 2025-04).",
          "error"
        );
        return;
      }
    }

    setIsApplyingFilters(true);
    try {
      if (cuotaFinalInput && cuotas.length === 0) {
        await dispatch(fetchCuotas());
      }
      setCuotaFinal(cuotaFinalInput);
      setTypeReport(typeReportInput);
    } finally {
      setIsApplyingFilters(false);
    }
  };

  const situacionesSelect = situaciones
    .filter(s => s.alias && !s.alias.includes("preinscrito"))
    .map(situ => ({ value: situ.id, label: situ.nombre }));

  return (

    <>
      {isPreparingPrint && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999
          }}
        >
          <div className="text-center text-white">
            <div
              className="spinner-border text-light"
              style={{ width: "4rem", height: "4rem" }}
            ></div>
            <p className="mt-3 fs-4">Preparando impresión…</p>
          </div>
        </div>
      )}
      <div className="list-container list1">
        <h1 className="table-title">REPORTE DE AFILIADOS</h1>
        <div className="list-content">
          <div className='row'>
            <div className="col-md-2">
              <Form.Label>Tipo cuota</Form.Label>
              <Form.Control
                as="select"
                className="form-control"
                size="sm"
                value={typeReportInput}
                onChange={(e) => setTypeReportInput(e.target.value)}
                disabled={isApplyingFilters}
              >
                {optionCuotas.map(optionC =>
                  <option key={optionC.value} value={optionC.value}>{optionC.label}</option>
                )}
              </Form.Control>
            </div>
            <Form.Group className='col-6  col-md-2'>
              <Form.Label>Cuota a consultar:</Form.Label>
              <Form.Control
                type="text"
                placeholder='AAAA-MM'
                name='cuotaFinal'
                value={cuotaFinalInput}
                onChange={handleCuotaChange}
                disabled={isApplyingFilters}
              />
            </Form.Group>

            <div className='col-6 col-md-2 mt-4'>
              <Select
                isMulti
                options={situacionesSelect}
                value={selectedSituaciones}
                onChange={handleFilterSituacionChange}
                placeholder="Filtrar por situación..."
                className="react-select-container"
                classNamePrefix="react-select"
                isDisabled={isApplyingFilters}
              />
            </div>
            <div className="col-6 col-md-2 d-flex align-items-center">
              <input
                type="checkbox"
                id="cumpleañeros"
                checked={cumpleañeros}
                onChange={(e) => setCumpleañeros(e.target.checked)}
                className="form-check-input me-1"
                disabled={isApplyingFilters}
              />
              <label htmlFor="cumpleañeros" className="form-check-label">
                Cumpleañeros
              </label>
            </div>
            <div className="col-6 col-md-4 mt-4">
              <input
                type="text"
                placeholder="Título del reporte"
                className="form-control"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                disabled={isApplyingFilters}
              />
            </div>
          </div>

          <div className="list-container-header d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-sm btn-primary me-2"
              onClick={handleApplyFilters}
              disabled={isApplyingFilters}
            >
              {isApplyingFilters ? 'Aplicando...' : 'Aplicar'}
            </button>

            <button
              title="Incluir líneas de firma"
              onClick={() => setIncludeSignatureLines(!includeSignatureLines)}
              className="icon-button btn-transparent"
              disabled={isApplyingFilters}
            >
              <i className={`bi ${includeSignatureLines ? 'bi-eye-slash' : 'bi-eye'}`}></i>
            </button>

            {/* ✅ NUEVO ICONO: SOLO SIN DEUDA */}
            <button
              title={soloSinDeuda ? "Mostrar todos" : "Mostrar solo sin deuda (deuda total = 0)"}
              onClick={() => setSoloSinDeuda(!soloSinDeuda)}
              className="icon-button btn-transparent"
              disabled={isApplyingFilters}
            >
              <i className={`bi ${soloSinDeuda ? 'bi-cash-coin' : 'bi-cash'}`}></i>
            </button>

            {/* NUEVO ICONO: incluir también sin deuda */}
            <button
              title={includeSinDeuda ? "Mostrar solo con deuda" : "Incluir también sin deuda"}
              onClick={() => setIncludeSinDeuda(!includeSinDeuda)}
              className="icon-button btn-transparent"
              disabled={isApplyingFilters}
            >
              <i className={`bi ${includeSinDeuda ? 'bi-people-fill' : 'bi-people'}`}></i>
            </button>

            <button
              title="Imprimir"
              onClick={async () => {
                setIsPreparingPrint(true);

                await new Promise(res => setTimeout(res, 100));

                setShowPreviewModal(true);

                setIsPreparingPrint(false);
              }}
              className="icon-button btn-transparent"
              disabled={isApplyingFilters}
            >
              <i className="bi bi-printer"></i>
            </button>

            <button
              title="Exportar a Excel"
              onDoubleClick={handleExport}
              className="icon-button btn-transparent"
              disabled={isApplyingFilters}
            >
              <i className="bi bi-file-spreadsheet"></i>
            </button>
          </div>

          <div className="col-6 col-md-2 d-flex align-items-center">
            <input
              type="checkbox"
              id="firma"
              checked={includeSignatureLines}
              onChange={(e) => setIncludeSignatureLines(e.target.checked)}
              className="form-check-input me-1"
              disabled={isApplyingFilters}
            />
            <label htmlFor="firma" className="form-check-label">
              Incluir casilla de firma
            </label>
          </div>

          {sortedAfiliados.length === 0 ? (
            <p>No hay afiliados disponibles.</p>
          ) : (
            <div className="table-container">
              <Table bordered hover size="sm">
                <thead style={{ backgroundColor: '#b2aea3' }}>
                  <tr>
                    <th>#</th>
                    {sortConfig.key === 'num_reg' ? (
                      <>
                        <th onClick={() => handleSort('num_reg')} style={{ cursor: 'pointer' }}>
                          Número Registro {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </th>
                        <th onClick={() => handleSort('nombre_completo')} style={{ cursor: 'pointer' }}>
                          Nombre Completo
                        </th>
                      </>
                    ) : (
                      <>
                        <th onClick={() => handleSort('nombre_completo')} style={{ cursor: 'pointer' }}>
                          Nombre Completo {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </th>
                        <th onClick={() => handleSort('num_reg')} style={{ cursor: 'pointer' }}>
                          Número Registro
                        </th>
                      </>
                    )}
                    <th>C.I.</th>
                    <th>Fecha Registro C.A.B.</th>
                    {cumpleañeros && <th>Fecha Nacimiento</th>}
                    <th>Residencia</th>
                    <th>Situación</th>
                    {cuotaFinal && (!typeReport || typeReport === 'cat') && <th>Deuda CAT</th>}
                    {cuotaFinal && (!typeReport || typeReport === 'cab') && <th>Deuda CAB</th>}
                    {cuotaFinal && <th>Deuda Total</th>}
                    <th>Siguiente Cuota</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAfiliados.map((afiliado, index) => (
                    <tr key={afiliado.id}>
                      <td>{index + 1}</td>
                      {sortConfig.key === 'num_reg' ? (
                        <>
                          <td>{afiliado.num_reg}</td>
                          <td>{`${apellidosNombreFullPersona(afiliado.persona)}`}</td>
                        </>
                      ) : (
                        <>
                          <td>{`${apellidosNombreFullPersona(afiliado.persona)}`}</td>
                          <td>{afiliado.num_reg}</td>
                        </>
                      )}
                      <td>{afiliado.persona.ci}</td>
                      <td>{formatearFechaSimple(afiliado.fecha_reg_cab)}</td>
                      {cumpleañeros && <td>{formatearFechaSimple(afiliado.persona.fecha_nac)}</td>}
                      <td>{afiliado.lugar_residencia?.nombre}</td>
                      <td>{afiliado.situacion.nombre || 'N/A'}</td>
                      {cuotaFinal && (!typeReport || typeReport === 'cat') && (
                        <td>{Number(afiliado.deudaCat || 0).toFixed(2)}</td>
                      )}
                      {cuotaFinal && (!typeReport || typeReport === 'cab') && (
                        <td>{Number(afiliado.deudaCab || 0).toFixed(2)}</td>
                      )}
                      {cuotaFinal && (
                        <td>{Number(afiliado.deudaTotal || 0).toFixed(2)}</td>
                      )}
                      <td>
                        {(!typeReport || typeReport === "cat") && (
                          <div>CAT: {afiliado.situacion.alias === "activo2" ? "sin pendientes" : getCuotaNombre(afiliado.sig_cuota_cat)}</div>
                        )}
                        {(!typeReport || typeReport === "cab") && (
                          <div>CAB: {afiliado.situacion.alias === "activo2" ? "sin pendientes" : getCuotaNombre(afiliado.sig_cuota_cab)}</div>
                        )}
                      </td>
                    </tr>
                  ))}

                  {/* Fila TOTAL GENERAL visible en la tabla */}
                  {cuotaFinal && sortedAfiliados.length > 0 && (
                    <tr>
                      <td></td>
                      {sortConfig.key === 'num_reg' ? (
                        <>
                          <td></td>
                          <td><strong>TOTAL GENERAL</strong></td>
                        </>
                      ) : (
                        <>
                          <td><strong>TOTAL GENERAL</strong></td>
                          <td></td>
                        </>
                      )}
                      <td></td>
                      <td></td>
                      {cumpleañeros && <td></td>}
                      <td></td>
                      <td></td>
                      {cuotaFinal && (!typeReport || typeReport === 'cat') && (
                        <td><strong>{totalDeudaCat.toFixed(2)}</strong></td>
                      )}
                      {cuotaFinal && (!typeReport || typeReport === 'cab') && (
                        <td><strong>{totalDeudaCab.toFixed(2)}</strong></td>
                      )}
                      {cuotaFinal && (
                        <td><strong>{totalDeudaTotal.toFixed(2)}</strong></td>
                      )}
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}

          <CustomPrintModal
            show={showPreviewModal}
            handleClose={() => setShowPreviewModal(false)}
            title1={`LISTA DE AFILIADOS${customTitle && `\n${customTitle}`}`}
            title2={`${cuotaFinal
              ? `Cuota(s): ${optionCuotas.find(oc => oc.value === typeReport)?.label} / No adeudo hasta: ${getCuotaNombre(cuotaFinal)}`
              : "Sin cuota consultada"
              }${selectedSituaciones.length === 0 ? "" : `\nAfiliados en situación: ${selectedSituaciones.map(s => s.label).join(", ")}`}`}
            title3={`Fecha de consulta: ${fechaHoraActualBoliviaSoloVista("soloFecha")} / Resultado: ${sortedAfiliados.length} registros`}
            content={getPreviewContent()}
          />
        </div>
      </div>
    </>
  );

};

export default AfiliadoReport;
